import React, { useState, useRef } from "react";
import { toast } from "react-toastify";
import useAxiosInstance from "../../hooks/useAxios";

const EditPPTDeck = () => {
  const axiosInstance = useAxiosInstance();
  const fileInputRef = useRef(null);

  const [deckId, setDeckId] = useState("");
  const [selectedFile, setSelectedFile] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDragging, setIsDragging] = useState(false);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.name.match(/\.(ppt|pptx)$/i)) {
        toast.error("Only PPT/PPTX files are allowed");
        e.target.value = "";
        return;
      }
      setSelectedFile(file);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) {
      if (!file.name.match(/\.(ppt|pptx)$/i)) {
        toast.error("Only PPT/PPTX files are allowed");
        return;
      }
      setSelectedFile(file);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleSubmit = async () => {
    if (!deckId.trim()) {
      toast.info("Please provide a deck ID");
      return;
    }
    if (!selectedFile) {
      toast.info("Please upload a PPT file");
      return;
    }

    const formData = new FormData();
    formData.append("deck_id", deckId.trim());
    formData.append("file", selectedFile);

    try {
      setIsSubmitting(true);
      // Mock API call — replace with actual endpoint
      const response = await axiosInstance.post(
        `presentation-creator/presentations/${deckId.trim()}/replace-ppt/` ,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } },
      );
      console.log(response.data);
      toast.success("PPT deck updated successfully!");
      setDeckId("");
      setSelectedFile(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
    } catch (err) {
      console.error("Upload error:", err);
      toast.error("Failed to update PPT deck");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="h-screen mr-2 overflow-y-auto">
      <div className="w-full p-4 my-4 bg-white border rounded-lg">
        <p className="mb-4 text-2xl font-medium text-black">Edit PPT Deck</p>
        <div className="flex flex-col gap-4 p-3 bg-gray-100 rounded-xl">
          {/* Deck ID Input */}
          <div className="flex flex-col gap-1">
            <label className="text-sm font-medium text-gray-700">Deck ID</label>
            <input
              type="text"
              value={deckId}
              onChange={(e) => setDeckId(e.target.value)}
              placeholder="Enter deck ID"
              className="px-3 py-2 text-sm bg-white border rounded-lg focus:outline-none focus:ring-2 focus:ring-black"
            />
          </div>

          {/* File Upload */}
          <div className="flex flex-col gap-1">
            <label className="text-sm font-medium text-gray-700">
              PPT File
            </label>
            <div
              onClick={() => fileInputRef.current?.click()}
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              className={`border-2 border-dashed rounded-lg p-6 flex flex-col items-center justify-center cursor-pointer transition-colors ${
                isDragging
                  ? "border-black bg-gray-200"
                  : "border-gray-300 bg-white hover:bg-gray-50"
              }`}
            >
              {selectedFile ? (
                <div className="flex flex-col items-center gap-1">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="w-8 h-8 text-orange-500"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                    />
                  </svg>
                  <p className="text-sm font-medium text-gray-800">
                    {selectedFile.name}
                  </p>
                  <p className="text-xs text-gray-500">
                    {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                  </p>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedFile(null);
                      if (fileInputRef.current) fileInputRef.current.value = "";
                    }}
                    className="mt-1 text-xs text-red-500 underline hover:text-red-700"
                  >
                    Remove
                  </button>
                </div>
              ) : (
                <div className="flex flex-col items-center gap-1 text-gray-500">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="w-8 h-8"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12"
                    />
                  </svg>
                  <p className="text-sm font-medium">
                    Click or drag & drop to upload
                  </p>
                  <p className="text-xs">PPT, PPTX files only</p>
                </div>
              )}
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept=".ppt,.pptx,application/vnd.ms-powerpoint,application/vnd.openxmlformats-officedocument.presentationml.presentation"
              onChange={handleFileChange}
              className="hidden"
            />
          </div>

          {/* Submit Button */}
          <div className="flex items-center">
            <button
              onClick={handleSubmit}
              disabled={isSubmitting || !deckId.trim() || !selectedFile}
              className="px-4 py-2 h-[42px] disabled:bg-gray-300 bg-black rounded-lg text-white font-semibold"
            >
              {isSubmitting ? "Uploading..." : "Upload"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditPPTDeck;
